import { boot, eraroCodesMap } from '@dazn/bootstrap-lib';

import { chapterNames } from '../../configuration/constants';
import { SetCurrentChapter } from '../../public-api/lib/SetCurrentChapter';
import { createInvalidChapterError } from '../chapterLoader/errors';
import {
    Configuration,
    ErrorHandlingModule,
    UserModule,
} from '../global-modules';
import { LoadEventContext } from '../lifecycle/lifecycle';
import { getChapterInfoFromPath } from '../utils/getChapterFromPath';
import { makeAbsolute } from '../utils/location';
import { normalizeChapter } from '../utils/normalizeChapter';

import { garbageCollector } from './garbageCollector';

const NEWS_SSR_LOCALES = ['pt-PT', 'it-IT'];

type ResourceStrings = boot.resourceStrings.Data;
type NormalChapterConfig = boot.config.NormalChapterConfig;
type ChapterName = boot.config.ChapterName;
type AnyCountry = boot.config.AnyCountry;
type Language = boot.config.Language;

type TriggerChapterDidLoadEvent = (context: LoadEventContext) => Promise<void>;

type VerifyChapterAuthorization = (chapterName: ChapterName) => ChapterName;
type GetChapterCountry = () => AnyCountry;
type SetChapterVersion = (
    chapter: ChapterName,
    version: string | null,
) => boolean;
type GetChapterLanguage = () => Language;
type DownloadChapterAndAddInPage = (
    chapterName: ChapterName,
    chapterCountry: AnyCountry,
    garbageCollector: () => void,
) => Promise<string | null>;
type GetChapterUrl = (
    normalizedChapter: NormalChapterConfig,
    chapterCountry: AnyCountry,
    chapterLanguage: Language,
) => string;
type GetResourceStrings = () => Promise<ResourceStrings> | ResourceStrings;

export interface ChapterHistoryProps {
    window: typeof window;
    configuration: Configuration;
    errorModule: ErrorHandlingModule;
    userModule: UserModule;
    verifyChapterAuthorization: VerifyChapterAuthorization;
    getChapterCountry: GetChapterCountry;
    setChapterVersion: SetChapterVersion;
    getChapterLanguage: GetChapterLanguage;
    downloadChapterAndAddInPage: DownloadChapterAndAddInPage;
    getChapterUrl: GetChapterUrl;
    getResourceStrings: GetResourceStrings;
    triggerChapterDidLoadEvent: TriggerChapterDidLoadEvent;
    getStartupNativeCountryOrMoon: () => boot.config.NativeCountryOrMoon;
    getStartupNativeCountryOrMoonOrMena: () => boot.config.NativeCountryOrMoonOrMena;
    isVip: () => boolean;
    getBrandName: () => boot.brand.BrandName;
}

export const createChapterHistory = ({
    window,
    configuration,
    errorModule,
    userModule,
    verifyChapterAuthorization,
    getChapterCountry,
    setChapterVersion,
    getChapterLanguage,
    downloadChapterAndAddInPage,
    getChapterUrl,
    getResourceStrings,
    triggerChapterDidLoadEvent,
    getStartupNativeCountryOrMoon,
    getStartupNativeCountryOrMoonOrMena,
    isVip,
    getBrandName,
}: ChapterHistoryProps) => {
    const chapterHistory: NormalChapterConfig[] = [];

    const getBrowserHistory = () => {
        const isFirstLoad = chapterHistory.length === 0;

        return {
            initialUpdate(newCurrentChapter: NormalChapterConfig) {
                const chapterUrl = getChapterUrl(
                    newCurrentChapter,
                    getChapterCountry(),
                    getChapterLanguage(),
                );
                const absoluteUrl = makeAbsolute(location, chapterUrl);

                if (!isFirstLoad) {
                    // push history
                    history.pushState(
                        newCurrentChapter.chapterData,
                        '',
                        absoluteUrl,
                    );
                } else {
                    // replace history for first page load
                    history.replaceState(
                        newCurrentChapter.chapterData,
                        '',
                        absoluteUrl,
                    );
                }
            },
            replace(newCurrentChapter: NormalChapterConfig, nextUrl: string) {
                if (!isFirstLoad) {
                    history.replaceState(
                        newCurrentChapter.chapterData,
                        '',
                        makeAbsolute(location, nextUrl),
                    );
                }
            },
        };
    };

    const getNormalisedAndVerifiedChapter = (
        newChapter: boot.config.ChapterNameLike,
    ) => {
        let chapterPath =
            chapterHistory.length === 0 && location ? location.pathname : '';
        // Temporarily fix only for edge case CORE-1380
        if (
            chapterHistory.length === 0 &&
            typeof newChapter !== 'string' &&
            typeof newChapter?.chapterPath === 'string'
        ) {
            chapterPath = newChapter.chapterPath;
        }
        // end of the fix
        const normalChapterOrNull = normalizeChapter(newChapter, chapterPath);

        // Bad chapter name
        if (!Boolean(normalChapterOrNull) && typeof newChapter === 'string') {
            throw createInvalidChapterError(`Unknown chapter: "${newChapter}"`);
        }

        const getDefaultChapterName = () =>
            getChapterInfoFromPath({
                configuration,
                isAuthorized: userModule.isAuthorized(),
                isActive: userModule.isActive,
                pathname: chapterPath,
                startupNativeCountryOrMoon: getStartupNativeCountryOrMoon(),
                startupNativeCountryOrMoonOrMena:
                    getStartupNativeCountryOrMoonOrMena(),
                isVip,
            }).chapterName;

        // we might get data that should be forwarded
        const chapterDataOrNull =
            (newChapter &&
                typeof newChapter === 'object' &&
                newChapter.chapterData) ||
            null;

        const normalChapter = normalChapterOrNull ?? {
            chapterName: getDefaultChapterName(),
            chapterData: chapterDataOrNull,
            chapterPath,
        };

        const { chapterName } = normalChapter;

        const verifiedChapterName = verifyChapterAuthorization(chapterName);
        if (verifiedChapterName === chapterName) {
            return normalChapter;
        }

        return normalizeChapter(
            {
                chapterName: verifiedChapterName,
                chapterPath:
                    configuration.chapters.homePage(verifiedChapterName),
                chapterData: {
                    deepLink: normalChapter,
                },
            },
            chapterPath,
        )!;
    };

    const onChapterInstalled = (
        {
            chapterName,
            chapterData,
            chapterPath,
        }: boot.config.NormalChapterConfig,
        {
            chapterCountry,
            chapterVersion,
        }: {
            chapterCountry: boot.config.AnyCountry;
            chapterVersion: string | null;
        },
    ) => {
        chapterHistory.push({
            chapterName,
            chapterData,
            chapterPath,
        });
        window.newrelic?.setCustomAttribute('chapter', chapterName);
        setChapterVersion(chapterName, chapterVersion);
        triggerChapterDidLoadEvent({
            chapter: chapterName,
            country: chapterCountry,
        });
    };

    const fetchNewChapter = (
        newCurrentChapter: boot.config.NormalChapterConfig,
        ignoreBrowserHistory: boolean,
    ) => {
        const browserHistory = getBrowserHistory();
        chapterHistory.push(newCurrentChapter);

        let nextUrl: string;
        let chapterCountry: AnyCountry;
        let chapterLanguage: Language;
        return Promise.resolve(null)
            .then(() =>
                newCurrentChapter.chapterName === chapterNames.ERROR
                    ? undefined
                    : userModule.fetchProfile().catch(error => {
                          errorModule.captureException(error);
                      }),
            )
            .then(() => {
                if (!ignoreBrowserHistory) {
                    browserHistory.initialUpdate(newCurrentChapter);
                }
                chapterCountry = getChapterCountry();
                chapterLanguage = getChapterLanguage();

                nextUrl = getChapterUrl(
                    newCurrentChapter,
                    chapterCountry,
                    chapterLanguage,
                );
            })
            .then(() => getResourceStrings())
            .then(() => {
                if (!ignoreBrowserHistory) {
                    browserHistory.replace(newCurrentChapter, nextUrl);
                }
            })
            .then(() =>
                downloadChapterAndAddInPage(
                    newCurrentChapter.chapterName,
                    chapterCountry,
                    () => garbageCollector(configuration.environment),
                ),
            )
            .then(chapterVersion => ({
                chapterCountry,
                chapterVersion,
            }));
    };

    const getCurrentChapter = (): NormalChapterConfig =>
        chapterHistory[chapterHistory.length - 1] ?? {
            // TODO: getChapterFromPath(location.pathname),
            chapterName: 'unknown',
            chapterPath: location.pathname,
            chapterData: null,
        };

    const setCurrentChapter: SetCurrentChapter = (
        nc,
        // FIXME: (Maxim) a hack to fix back button issue CORE-939
        ignoreBrowserHistory = false,
    ) => {
        const newChapter = getNormalisedAndVerifiedChapter(nc);
        if (!newChapter) {
            return Promise.resolve();
        }

        // Foxsports is SSR-only, use window.location.href for full page navigation
        const brandName = getBrandName();
        if (brandName === 'foxsports') {
            const lang = getChapterLanguage().toLowerCase();
            const country = getChapterCountry().toUpperCase();
            const locale = `${lang}-${country}`;
            const localizedPath =
                newChapter.chapterPath || `/${locale}/${chapterNames.NEWS}`;

            // Only redirect if we're not already on the target path
            if (window.location.pathname !== localizedPath) {
                window.location.href = localizedPath;
                return Promise.resolve();
            }

            // Already on the correct page (SSR rendered), mark chapter as installed
            const chapterCountry = getChapterCountry();
            onChapterInstalled(newChapter, {
                chapterCountry,
                chapterVersion: null, // SSR doesn't have a chapter version
            });
            return Promise.resolve();
        }

        if (newChapter.chapterName === chapterNames.NEWS) {
            const lang = getChapterLanguage().toLowerCase();
            const country = getChapterCountry().toUpperCase();
            const locale = `${lang}-${country}`;
            if (
                NEWS_SSR_LOCALES.map(l => l.toLowerCase()).includes(
                    locale.toLowerCase(),
                )
            ) {
                const localizedPath =
                    newChapter.chapterPath || `/${locale}/${chapterNames.NEWS}`;
                window.location.href = localizedPath;
                return Promise.resolve();
            }
        }
        return fetchNewChapter(newChapter, ignoreBrowserHistory)
            .then(chapterMetaInfo => {
                onChapterInstalled(newChapter, chapterMetaInfo);
            })
            .catch((error: Error) => {
                errorModule.handleError(
                    eraroCodesMap.category.ChapterLoading,
                    undefined,
                    undefined,
                    newChapter.chapterName,
                    error,
                );
            });
    };

    return {
        getCurrentChapter,
        setCurrentChapter,
        fetchNewChapter,
        getNormalisedAndVerifiedChapter,
        onChapterInstalled,
    };
};
